<script lang="ts">
import type { StyleValue } from 'vue';
import type { NxDatePreset } from './datePresets';

export type DateTimeInputProps = {
  /** Identifier of the date field (the first of the pair) — this is what an enclosing `NxLabel` points at. */
  id?: string;
  /** Display format of the date part, see `NxDateInput` (`YYYY`, `MM`, `DD`). Defaults to the locale format. */
  dateFormat?: string;
  /** Display format of the time part, see `NxTimeInput` (`HH`, `mm`, `A`/`a`). Defaults to the locale format. */
  timeFormat?: string;
  /** Placeholder of the date part. Defaults to the date format. */
  datePlaceholder?: string;
  /** Placeholder of the time part. Defaults to the time format. */
  timePlaceholder?: string;
  disabled?: boolean;
  /** BCP-47 locale for both parts. Defaults to the i18n injection locale. */
  locale?: string;
  /** ISO local datetime `YYYY-MM-DDTHH:mm` lower bound (inclusive). */
  minValue?: string;
  /** ISO local datetime `YYYY-MM-DDTHH:mm` upper bound (inclusive). */
  maxValue?: string;
  /**
   * Time part (`HH:mm`) that completes the value while the user has picked a
   * date but no time yet. Defaults to `'00:00'`.
   */
  defaultTime?: string;
  wrapperStyle?: StyleValue;
  height?: InputHeight;
  /** Forwarded to both parts, see `NxDateInput` / `NxTimeInput`. */
  revealIcon?: boolean;
  /**
   * Times offered as one-click chips under the field (`HH:mm`), e.g. the
   * start of the working day. Picking one sets only the time part.
   */
  timePresets?: readonly string[];
  /** Accessible name of the time preset group. */
  timePresetsLabel?: string;
  /** Forwarded to the date part, see `NxDateInput`. */
  markWeekends?: boolean;
  /** Forwarded to the date part, see `NxDateInput`. */
  holidays?: Readonly<Record<string, string>>;
  /**
   * One-click days offered as chips under the field, see `defaultDatePresets()`.
   * A preset with a `time` sets that time too; without one the time in the
   * field stays. A chip shows as selected while the value matches it.
   */
  datePresets?: readonly NxDatePreset[];
  /** Accessible name of the date preset group. */
  datePresetsLabel?: string;
  /** Reference day (ISO `YYYY-MM-DD`) for the date presets; forwarded to the date part. Defaults to the current day. */
  today?: string;
  /** Forwarded to the date part: the distance to today follows the date („08.07.2026 · morgen“). */
  relative?: boolean;
  /** Warn under the field on a weekend day or holiday, with a button to the next working day (the time stays). */
  warnNonWorkingDay?: boolean;
  /** Reason shown for a Saturday or Sunday (a holiday shows its own name). */
  weekendLabel?: string;
  /** Text after the reason („Wochenende – …“). */
  nonWorkingDayHint?: string;
  /** Label of the button that moves the date to the next working day. */
  nextWorkingDayLabel?: string;
};

/** Splits an ISO local datetime into its `YYYY-MM-DD` and `HH:mm` parts. Anything unparseable yields `null` parts. */
export function splitIsoDateTime(value: string | null | undefined): {
  date: string | null;
  time: string | null;
} {
  if (!value) return { date: null, time: null };
  const m = /^(\d{4}-\d{2}-\d{2})(?:T(\d{2}:\d{2}))?/.exec(value);
  if (!m) return { date: null, time: null };
  return { date: m[1], time: m[2] ?? null };
}
</script>

<script setup lang="ts">
/**
 * One field for a local date **and** time.
 *
 * Composes `NxDateInput` and `NxTimeInput` (both keep their own parsing,
 * dropdowns and keyboard handling) into a single control that models an ISO
 * local datetime string `YYYY-MM-DDTHH:mm` — the same shape a native
 * `<input type="datetime-local">` uses and the natural join of the two parts'
 * own models (`YYYY-MM-DD` and `HH:mm`). No timezone, no seconds. `null` when
 * no date is set.
 *
 * Semantics of a half-filled field:
 * - date set, time empty → the value is completed with `defaultTime`.
 * - date cleared → the value becomes `null`; the time the user typed stays
 *   visible so re-picking a date restores it.
 */
import { computed, inject, nextTick, ref, useTemplateRef, watch } from 'vue';
import { i18nInjectionKey, InputHeight, loadingStateInjectionKey } from './internal';
import { NxBanner } from '../Banner';
import { NxButton } from '../Button';
import NxChip from '../Chip/NxChip.vue';
import NxChipGroup from '../Chip/NxChipGroup.vue';
import { nextWorkingDay, nonWorkingDayReason } from './datePresets';
import { todayIso } from './dateRangePresets';
import NxDateInput from './NxDateInput.vue';
import NxTimeInput from './NxTimeInput.vue';

const props = withDefaults(defineProps<DateTimeInputProps>(), {
  id: undefined,
  dateFormat: undefined,
  timeFormat: undefined,
  datePlaceholder: undefined,
  timePlaceholder: undefined,
  locale: undefined,
  minValue: undefined,
  maxValue: undefined,
  defaultTime: '00:00',
  wrapperStyle: undefined,
  height: undefined,
  revealIcon: false,
  timePresets: undefined,
  timePresetsLabel: 'Uhrzeit-Schnellwahl',
  markWeekends: false,
  holidays: undefined,
  datePresets: undefined,
  datePresetsLabel: 'Datum-Schnellwahl',
  today: undefined,
  relative: false,
  warnNonWorkingDay: false,
  weekendLabel: 'Wochenende',
  nonWorkingDayHint: 'an diesem Tag arbeitet niemand.',
  nextWorkingDayLabel: 'Nächster Werktag',
});

/** ISO local datetime `YYYY-MM-DDTHH:mm`, or `null` when no date is set. */
const valueModel = defineModel<string | null>({ required: true });

const i18nInjection = inject(i18nInjectionKey)!;
const loadingState = inject(loadingStateInjectionKey, null);
const disabled = computed(() => !!(props.disabled || loadingState?.status.value?.disabled));
const locale = computed(() => props.locale ?? i18nInjection.locale.value);

const initial = splitIsoDateTime(valueModel.value);
const datePart = ref<string | null>(initial.date);
const timePart = ref<string | null>(initial.time);

watch(
  () => valueModel.value,
  (v) => {
    const parts = splitIsoDateTime(v);
    datePart.value = parts.date;
    // A cleared value keeps the pending time (see the semantics above); a set
    // value is the source of truth for both parts.
    if (parts.date) timePart.value = parts.time;
  }
);

const emitJoined = () => {
  const next = datePart.value ? `${datePart.value}T${timePart.value ?? props.defaultTime}` : null;
  if (next !== valueModel.value) valueModel.value = next;
};

const dateModel = computed<string | null>({
  get: () => datePart.value,
  set: (v) => {
    datePart.value = v;
    emitJoined();
  },
});

const timeModel = computed<string | null>({
  get: () => timePart.value,
  set: (v) => {
    timePart.value = v;
    emitJoined();
  },
});

/** A preset outside the bounds of the picked day is offered disabled. */
function presetDisabled(time: string): boolean {
  if (disabled.value) return true;
  if (timeMin.value && time < timeMin.value) return true;
  if (timeMax.value && time > timeMax.value) return true;
  return false;
}

const minParts = computed(() => splitIsoDateTime(props.minValue));
const maxParts = computed(() => splitIsoDateTime(props.maxValue));

/** The time bounds only bite on the bound's own day; on any other day every time is fine. */
const timeMin = computed(() =>
  minParts.value.date && datePart.value === minParts.value.date
    ? (minParts.value.time ?? undefined)
    : undefined
);
const timeMax = computed(() =>
  maxParts.value.date && datePart.value === maxParts.value.date
    ? (maxParts.value.time ?? undefined)
    : undefined
);

/** Whether `date` at `time` lies outside `minValue`/`maxValue`. */
function outOfBounds(date: string, time: string): boolean {
  const min = minParts.value;
  const max = maxParts.value;
  if (min.date && (date < min.date || (date === min.date && !!min.time && time < min.time))) {
    return true;
  }
  return !!max.date && (date > max.date || (date === max.date && !!max.time && time > max.time));
}

const reference = computed(() => props.today ?? todayIso());

function datePresetSelected(preset: NxDatePreset): boolean {
  const date = preset.date(reference.value);
  if (date === null) return datePart.value === null;
  return date === datePart.value && (preset.time === undefined || preset.time === timePart.value);
}

function datePresetDisabled(preset: NxDatePreset): boolean {
  if (disabled.value) return true;
  const date = preset.date(reference.value);
  if (date === null) return false;
  return outOfBounds(date, preset.time ?? timePart.value ?? props.defaultTime);
}

/** Sets the day and — when the preset carries one — the time; `null` clears like an emptied date part. */
function applyDatePreset(preset: NxDatePreset) {
  const date = preset.date(reference.value);
  datePart.value = date;
  if (date !== null && preset.time !== undefined) timePart.value = preset.time;
  emitJoined();
}

/** Holiday name or weekend label while the date is no working day — only with `warnNonWorkingDay`. */
const nonWorkingReason = computed(() =>
  props.warnNonWorkingDay && datePart.value
    ? nonWorkingDayReason(datePart.value, props.holidays, props.weekendLabel)
    : null
);

const nextWorkingDate = computed(() =>
  nonWorkingReason.value && datePart.value ? nextWorkingDay(datePart.value, props.holidays) : null
);

const nextWorkingDisabled = computed(
  () =>
    disabled.value ||
    !nextWorkingDate.value ||
    outOfBounds(nextWorkingDate.value, timePart.value ?? props.defaultTime)
);

const rootRef = useTemplateRef<HTMLElement>('root');

async function moveToNextWorkingDay() {
  const next = nextWorkingDate.value;
  if (!next) return;
  dateModel.value = next;
  // The banner — and with it the focused button — goes away with the
  // non-working day: hand focus to the date part instead of dropping it on the
  // page. After the render, so the date part copies the new day into its text.
  await nextTick();
  rootRef.value?.querySelector<HTMLInputElement>('input')?.focus();
}
</script>

<template>
  <div ref="root" class="nx-date-time-input" :style="props.wrapperStyle">
    <NxDateInput
      :id="props.id"
      v-model="dateModel"
      class="date-part"
      :format="props.dateFormat"
      :placeholder="props.datePlaceholder"
      :disabled="disabled"
      :locale="locale"
      :min-value="minParts.date ?? undefined"
      :max-value="maxParts.date ?? undefined"
      :height="props.height"
      :reveal-icon="props.revealIcon"
      :mark-weekends="props.markWeekends"
      :holidays="props.holidays"
      :today="props.today"
      :relative="props.relative"
    />
    <NxTimeInput
      v-model="timeModel"
      class="time-part"
      :format="props.timeFormat"
      :placeholder="props.timePlaceholder"
      :disabled="disabled"
      :locale="locale"
      :min-value="timeMin"
      :max-value="timeMax"
      :height="props.height"
      :reveal-icon="props.revealIcon"
    />
    <NxChipGroup
      v-if="props.datePresets?.length"
      class="date-presets"
      :aria-label="props.datePresetsLabel"
    >
      <NxChip
        v-for="preset in props.datePresets"
        :key="preset.id"
        size="xs"
        :selected="datePresetSelected(preset)"
        :aria-pressed="datePresetSelected(preset)"
        :disabled="datePresetDisabled(preset)"
        @click="applyDatePreset(preset)"
      >
        {{ preset.label }}
      </NxChip>
    </NxChipGroup>
    <NxChipGroup
      v-if="props.timePresets?.length"
      class="time-presets"
      :aria-label="props.timePresetsLabel"
    >
      <NxChip
        v-for="time in props.timePresets"
        :key="time"
        size="xs"
        :selected="timePart === time"
        :aria-pressed="timePart === time"
        :disabled="presetDisabled(time)"
        @click="timeModel = time"
      >
        {{ time }}
      </NxChip>
    </NxChipGroup>
    <!-- Mounted while the warning is enabled, so an appearing warning is
         announced; empty, it takes no space. -->
    <div v-if="props.warnNonWorkingDay" class="non-working-day" aria-live="polite">
      <NxBanner v-if="nonWorkingReason" variant="warning" class="non-working-day-banner">
        <b>{{ nonWorkingReason }}</b>
        {{ `– ${props.nonWorkingDayHint}` }}
        <template #buttons>
          <NxButton
            height="xs"
            variant="outline"
            :disabled="nextWorkingDisabled"
            @click="moveToNextWorkingDay"
          >
            {{ props.nextWorkingDayLabel }}
          </NxButton>
        </template>
      </NxBanner>
    </div>
  </div>
</template>

<style scoped>
.nx-date-time-input {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
  /* No row gap: the rows under the field space themselves (below), so an empty
     live region adds none. */
  gap: 0 var(--nxt-space-2xs);
  width: 100%;
}

.date-presets,
.time-presets,
.non-working-day {
  grid-column: 1 / -1;
}

.date-presets,
.time-presets,
.non-working-day-banner {
  margin-block-start: var(--nxt-space-2xs);
}
</style>
